import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
// ── Shared response schemas ─────────────────────────────────────────────────
const ErrorResponseSchema = z.object({
    message: z.string(),
    statusCode: z.number(),
});
// ── Workspace ───────────────────────────────────────────────────────────────
const PublicApiWorkspaceSchema = z.object({
    id: z.string(),
    name: z.string(),
});
const CreateWorkspaceBodySchema = z.object({
    name: z.string().min(1),
});
// ── Profile ─────────────────────────────────────────────────────────────────
const PublicApiProductSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    position: z.number(),
});
const PublicApiProfileSchema = z.object({
    id: z.string(),
    companyDescription: z.string().nullable(),
    onboardingCompleted: z.boolean(),
    markets: z.array(z.string()),
    riskClassifications: z.array(z.string()),
    deviceCharacteristics: z.array(z.string()),
    therapeuticAreas: z.array(z.string()),
    products: z.array(PublicApiProductSchema),
});
const UpdateProfileBodySchema = z.object({
    companyDescription: z.string().nullable().optional(),
    markets: z.array(z.string()).optional(),
    riskClassifications: z.array(z.string()).optional(),
    deviceCharacteristics: z.array(z.string()).optional(),
    therapeuticAreas: z.array(z.string()).optional(),
    products: z
        .array(z.object({
        id: z.string().optional(),
        name: z.string(),
        description: z.string().nullable().optional(),
        position: z.number().optional(),
    }))
        .optional(),
});
// ── Subscriptions ───────────────────────────────────────────────────────────
const SubscriptionStatusSchema = z.enum([
    'subscribed',
    'unsubscribed',
    'unknown',
]);
const PublicApiSourceSubscriptionSchema = z.object({
    sourceId: z.string(),
    sourceTitle: z.string(),
    geography: z.string(),
    status: SubscriptionStatusSchema,
});
const PublicApiReferenceSubscriptionSchema = z.object({
    referenceId: z.string(),
    referenceTitle: z.string(),
    referenceExternalId: z.string(),
    sourceId: z.string(),
    status: z.enum(['subscribed', 'unsubscribed']),
});
const PublicApiSubscriptionsResponseSchema = z.object({
    sources: z.array(PublicApiSourceSubscriptionSchema),
    references: z.array(PublicApiReferenceSubscriptionSchema),
});
const UpdateSubscriptionsBodySchema = z.object({
    sources: z
        .array(z.object({
        sourceId: z.string(),
        status: z.enum(['subscribed', 'unsubscribed']),
    }))
        .optional(),
    references: z
        .array(z.object({
        referenceId: z.string(),
        status: z.enum(['subscribed', 'unsubscribed']),
    }))
        .optional(),
});
// ── References ──────────────────────────────────────────────────────────────
const PublicApiReferenceSourceSchema = z.object({
    id: z.string(),
    title: z.string(),
    geography: z.string(),
    institutionName: z.string().nullable(),
});
const PublicApiReferenceSchema = z.object({
    id: z.string(),
    externalId: z.string(),
    title: z.string(),
    type: z.string().nullable(),
    url: z.string().nullable(),
    source: PublicApiReferenceSourceSchema,
});
const PublicApiReferenceVersionSchema = z.object({
    id: z.string(),
    effectiveDate: z.string().nullable(),
    status: z.string().nullable(),
    url: z.string().nullable(),
    sharedFile: z
        .object({
        id: z.string(),
        name: z.string(),
        signedUrl: z.string(),
    })
        .nullable(),
    update: z
        .object({
        id: z.string(),
        updateType: z.string().nullable(),
        shortSummary: z.string().nullable(),
        publicationDate: z.string(),
    })
        .nullable(),
});
const PublicApiReferenceDetailSchema = PublicApiReferenceSchema.extend({
    description: z.string().nullable(),
    versions: z.array(PublicApiReferenceVersionSchema),
});
// ── Updates ─────────────────────────────────────────────────────────────────
const PublicApiUpdateSchema = z.object({
    id: z.string(),
    kind: z.string(),
    reference: z
        .object({
        id: z.string(),
        externalId: z.string(),
        title: z.string(),
        type: z.string().nullable(),
        source: PublicApiReferenceSourceSchema,
    })
        .nullable(),
    update: z.object({
        updateType: z.string().nullable(),
        title: z.string().nullable(),
        shortSummary: z.string().nullable(),
        summary: z.string().nullable(),
        publicationDate: z.string(),
    }),
    context: z
        .object({
        status: z.string(),
        priority: z.string().nullable(),
        impactSummary: z.string().nullable(),
        suggestedActions: z.string().nullable(),
    })
        .nullable(),
});
const PublicApiUpdateDetailSchema = PublicApiUpdateSchema.extend({
    update: z.object({
        updateType: z.string().nullable(),
        title: z.string().nullable(),
        shortSummary: z.string().nullable(),
        summary: z.string().nullable(),
        content: z.string().nullable(),
        publicationDate: z.string(),
        sharedFile: z
            .object({
            id: z.string(),
            name: z.string(),
            signedUrl: z.string(),
        })
            .nullable(),
    }),
    currentVersion: z
        .object({
        id: z.string(),
        referenceId: z.string(),
    })
        .nullable(),
    previousVersion: z
        .object({
        id: z.string(),
        referenceId: z.string(),
    })
        .nullable(),
    redline: z
        .object({
        html: z.string().nullable(),
        status: z.string(),
    })
        .nullable(),
});
const ListUpdatesQuerySchema = z.object({
    status: z.string().optional(),
    priority: z.string().optional(),
    search: z.string().optional(),
    type: z.string().optional(),
    documentType: z.string().optional(),
    source: z.string().optional(),
    geography: z.string().optional(),
    publicationDateFrom: z.string().optional(),
    publicationDateTo: z.string().optional(),
    effectiveDateFrom: z.string().optional(),
    effectiveDateTo: z.string().optional(),
});
const ListReferencesQuerySchema = z.object({
    search: z.string().optional(),
    source: z.string().optional(),
    type: z.string().optional(),
});
// ── Contract ────────────────────────────────────────────────────────────────
export const publicApiContract = c.router({
    // Workspace management
    listWorkspaces: {
        method: 'GET',
        path: '/workspaces',
        responses: {
            200: z.array(PublicApiWorkspaceSchema),
            401: ErrorResponseSchema,
        },
    },
    createWorkspace: {
        method: 'POST',
        path: '/workspaces',
        body: CreateWorkspaceBodySchema,
        responses: {
            201: PublicApiWorkspaceSchema,
            401: ErrorResponseSchema,
            400: ErrorResponseSchema,
        },
    },
    deleteWorkspace: {
        method: 'DELETE',
        path: '/workspaces/:workspaceId',
        body: z.undefined(),
        responses: {
            204: z.undefined(),
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
    },
    // Profile
    getProfile: {
        method: 'GET',
        path: '/workspaces/:workspaceId/profile',
        responses: {
            200: PublicApiProfileSchema,
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
    },
    updateProfile: {
        method: 'PATCH',
        path: '/workspaces/:workspaceId/profile',
        body: UpdateProfileBodySchema,
        responses: {
            200: PublicApiProfileSchema,
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
    },
    generateProfile: {
        method: 'POST',
        path: '/workspaces/:workspaceId/profile/generate',
        body: z.object({ url: z.string().min(1) }),
        responses: {
            200: PublicApiProfileSchema,
            400: ErrorResponseSchema,
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
        },
    },
    // Subscriptions
    getSubscriptions: {
        method: 'GET',
        path: '/workspaces/:workspaceId/subscriptions',
        responses: {
            200: PublicApiSubscriptionsResponseSchema,
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
        },
    },
    updateSubscriptions: {
        method: 'PATCH',
        path: '/workspaces/:workspaceId/subscriptions',
        body: UpdateSubscriptionsBodySchema,
        responses: {
            200: z.object({ updated: z.number() }),
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
        },
    },
    generateSubscriptions: {
        method: 'POST',
        path: '/workspaces/:workspaceId/subscriptions/generate',
        body: z.undefined(),
        responses: {
            200: z.object({ success: z.boolean() }),
            400: ErrorResponseSchema,
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
        },
    },
    // References
    listReferences: {
        method: 'GET',
        path: '/workspaces/:workspaceId/references',
        query: ListReferencesQuerySchema,
        responses: {
            200: z.array(PublicApiReferenceSchema),
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
        },
    },
    getReferenceDetail: {
        method: 'GET',
        path: '/workspaces/:workspaceId/references/:referenceId',
        responses: {
            200: PublicApiReferenceDetailSchema,
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
    },
    // Updates
    listUpdates: {
        method: 'GET',
        path: '/workspaces/:workspaceId/updates',
        query: ListUpdatesQuerySchema,
        responses: {
            200: z.array(PublicApiUpdateSchema),
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
        },
    },
    getUpdateDetail: {
        method: 'GET',
        path: '/workspaces/:workspaceId/updates/:updateId',
        responses: {
            200: PublicApiUpdateDetailSchema,
            401: ErrorResponseSchema,
            403: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
    },
}, { pathPrefix: '/api' });
//# sourceMappingURL=publicApi.js.map